import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import SearchInput from "../../../../../components/base/input/search_input";
import {Row} from "reactstrap";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../containers/data-grid";
import {AccountReceivableDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes} from "../../../../../../packages/data-grid";
import classnames from "classnames";
import {ChevronRightRounded} from "@mui/icons-material";
import Api from "../../../../../../core/services/api/api";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {formatMoney} from "../../../../../../core/services/utils/utils";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {Link} from "react-router-dom";
import {Button} from "@mui/material";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";

const orderByDictionary = {
    customer: 'CustomerName',
    future: 'Future',
    current: 'Current',
    over30: 'Over30',
    over60: 'Over60',
    over90: 'Over90',
    over120: 'Over120',
    balance: 'Balance',
    lastPaymentDate: 'LastPaymentDate',
    lastPaymentAmount: 'LastPaymentAmount',
    lastInvoiceDate: 'LastInvoice',
    averageDaysToPay: 'AverageDaysToPay',
}


const dataGridSaveConfig = {
    name: 'AccountReceivableViewDataGrid',
    version: 1,
}

const AccountReceivablesView = () => {
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('account-receivables-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.accountingAccountReceivables);
        configureBehavioralTool((tags) => tags.accountingAccountReceivables);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Customer",
                name: AccountReceivableDataGridCellKeys.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 170,
            },
            {
                title: "Future",
                name: AccountReceivableDataGridCellKeys.future,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Current",
                name: AccountReceivableDataGridCellKeys.current,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Over 30",
                name: AccountReceivableDataGridCellKeys.over30,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Over 60",
                name: AccountReceivableDataGridCellKeys.over60,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Over 90",
                name: AccountReceivableDataGridCellKeys.over90,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Over 120",
                name: AccountReceivableDataGridCellKeys.over120,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Balance",
                name: AccountReceivableDataGridCellKeys.balance,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Last Payment Date",
                name: AccountReceivableDataGridCellKeys.lastPaymentDate,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
                width: 155,
            },
            {
                title: "Last Payment Amount",
                name: AccountReceivableDataGridCellKeys.lastPaymentAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 165,
            },
            {
                title: "Last Invoice Date",
                name: AccountReceivableDataGridCellKeys.lastInvoiceDate,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
                width: 145,
            },
            {
                title: "Terms",
                name: AccountReceivableDataGridCellKeys.terms,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            {
                title: "Credit Limit",
                name: AccountReceivableDataGridCellKeys.creditLimit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
                width: 100,
            },
            {
                title: "Average days to pay",
                name: AccountReceivableDataGridCellKeys.averageDaysToPay,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 155,
            },

        ], [])

    /**
     * Prepares the api call request body
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        return {
            keyword: args.filters?.keyword,
            pagination: args.paginationInfo,
            ...(!!args.orderBy ? {
                sorts: [
                    Object.fromEntries(Object.entries(args.orderBy).map(([key, value]) =>
                        [key === "descending"
                            ? "isDesc"
                            : "property",
                            (value === true || value === false)
                                ? value
                                : orderByDictionary[value]])
                    )
                ]
            } : {})
        }
    }, [])

    /**
     * Searches for list of account receivable either for exporting or for showing the list of items in the view
     * Calls the api regarding this action and updated the pagination info and sets the [reports] value in the inner state if not for exporting
     * Otherwise, returns the crud response
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getARAgedList(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {},
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportAccountReceivables = operatorAccessLevels.packages.sales.exportAccountReceivables;

    /**
     * Triggers the search over AR list by changing the url query params
     * @param keyword
     */
    const onKeywordChanged = useCallback((keyword) => {
        searchData.setFilters({keyword: keyword?.trim()});
    }, [searchData])

    /**
     * Formats the list of lost search reports into csv format
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Account Receivable Aged List`;
        return {
            title: exportTitle,
            body: list?.map((item, index) => ({
                "": index + 1,
                "Customer": item.customerName ?? '--',
                'Future': formatMoney(item.future) ?? '--',
                'Current': formatMoney(item.current) ?? '--',
                'Over 30': formatMoney(item.over30) ?? '--',
                'Over 60': formatMoney(item.over60) ?? '--',
                'Over 90': formatMoney(item.over90) ?? '--',
                'Over 120': formatMoney(item.over120) ?? '--',
                'Balance': formatMoney(item.balance) ?? '--',
                'Last Payment Date': DateUtils.serverToLocal(item.lastPaymentDate).format(DateUtils.dateFormat) ?? '--',
                'Last Payment Amount': formatMoney(item.lastPaymentAmount) ?? '--',
                'Last Invoice Date': DateUtils.serverToLocal(item.lastInvoice).format(DateUtils.dateFormat) ?? '--',
                'Terms': item.terms ?? '--',
                'Credit Limit': formatMoney(item.creditLimit) ?? '--',
                'Average days to pay': item.averageDaysToPay ?? '--',
            })) ?? [],
        }
    }, [])

    /**
     * Exports the requests using the same search filters to csv
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: searchData.paginationInfo.length,
            currenPage: 1,
        };
        const response = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
        if (!isMounted())
            return;
        if (!response?.resultFlag || !response?.data?.items?.length)
            return;
        return formatExportingData(response.data.items);
    }, [searchData, fetchEntries, isMounted, formatExportingData])

    /**
     * Memo version of table row records used in data grid
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return data?.map(ar => ({
            key: ar.id,
            data: ar,
            cells: {
                [AccountReceivableDataGridCellKeys.customer]:
                    ar?.customerId
                        ? <Button
                            className={classnames('part-number-text-button')}
                            variant={'text'}
                            component={Link}
                            to={routeFunctions.customer.accounting.accountReceivableTransactions(ar?.customerId ?? 0)}
                        >
                            <p>{ar.customerName}</p>
                            <ChevronRightRounded className='icon small' color={"primary"}/>
                        </Button>
                        : ar.customerName ?? "",
                [AccountReceivableDataGridCellKeys.future]: ar?.future,
                [AccountReceivableDataGridCellKeys.current]: ar?.current,
                [AccountReceivableDataGridCellKeys.over30]: ar?.over30,
                [AccountReceivableDataGridCellKeys.over60]: ar?.over60,
                [AccountReceivableDataGridCellKeys.over90]: ar?.over90,
                [AccountReceivableDataGridCellKeys.over120]: ar?.over120,
                [AccountReceivableDataGridCellKeys.balance]: ar?.balance,
                [AccountReceivableDataGridCellKeys.lastPaymentDate]: ar?.lastPaymentDate,
                [AccountReceivableDataGridCellKeys.lastPaymentAmount]: ar?.lastPaymentAmount,
                [AccountReceivableDataGridCellKeys.lastInvoiceDate]: ar?.lastInvoice,
                [AccountReceivableDataGridCellKeys.terms]: ar?.terms,
                [AccountReceivableDataGridCellKeys.creditLimit]: ar?.creditLimit,
                [AccountReceivableDataGridCellKeys.averageDaysToPay]: Math.ceil(ar?.averageDaysToPay),
            }
        })) ?? []
    }, [data])

    return (
        <>
            <Row className='no-gutters w-100 justify-content-between align-items-center mt-4 mb-5'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                    Account Receivable
                </h2>
                <div/>
            </Row>
            <SearchInput
                initialValue={searchData.filters?.keyword ?? undefined}
                loading={searchData.loading}
                autoSearch
                search={onKeywordChanged}
                label={'Keyword'}
            />
            <div className='w-100 my-5'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions={true}
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportAccountReceivables ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}

export default AccountReceivablesView;
